import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd } from '@theme/utilities';



const FJ_MENU_WIDGET_HIDE_ATTR = 'data-fj-hidden-by-menu';

function fjSetMobileMenuWidgetState(isOpen) {
  document.documentElement.classList.toggle('fj-mobile-menu-open', isOpen);
  document.body?.classList.toggle('fj-mobile-menu-open', isOpen);

  window.clearInterval(window.__fjMobileMenuHideWidgetsInterval);

  if (isOpen) {
    fjSetFloatingSupportWidgetsHidden(true);

    let runs = 0;
    window.__fjMobileMenuHideWidgetsInterval = window.setInterval(() => {
      const menuStillOpen = document.documentElement.classList.contains('fj-mobile-menu-open') || document.body?.classList.contains('fj-mobile-menu-open');
      if (!menuStillOpen || runs >= 28) {
        window.clearInterval(window.__fjMobileMenuHideWidgetsInterval);
        return;
      }
      fjSetFloatingSupportWidgetsHidden(true);
      runs += 1;
    }, 250);

    [50, 150, 350, 700, 1200, 2000, 3500].forEach((delay) => window.setTimeout(() => fjSetFloatingSupportWidgetsHidden(true), delay));
    return;
  }

  fjSetFloatingSupportWidgetsHidden(false);
}

function fjNormalizeMarkerValue(value) {
  if (!value) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'object' && 'baseVal' in value) return value.baseVal || '';
  return String(value || '');
}

function fjElementMarker(element) {
  if (!(element instanceof HTMLElement)) return '';

  const parts = [
    element.tagName,
    element.id,
    fjNormalizeMarkerValue(element.className),
    element.getAttribute('aria-label'),
    element.getAttribute('title'),
    element.getAttribute('href'),
    element.getAttribute('src'),
    element.getAttribute('data-testid'),
    element.getAttribute('data-test-id'),
    element.getAttribute('data-widget'),
    element.getAttribute('data-app'),
    element.getAttribute('data-name'),
  ];

  try {
    Object.keys(element.dataset || {}).forEach((key) => {
      parts.push(key, element.dataset[key]);
    });
  } catch (error) {
    // Dataset can be unavailable on some injected widgets. Ignore safely.
  }

  const text = (element.innerText || element.textContent || '').replace(/\s+/g, ' ').trim();
  if (text && text.length <= 120) parts.push(text);

  return parts.filter(Boolean).join(' ').toLowerCase();
}

function fjIsInsideProtectedArea(element) {
  if (!(element instanceof HTMLElement)) return true;

  return !!element.closest([
    'header-drawer',
    '.header-drawer',
    '.menu-drawer',
    '.menu-drawer__content',
    '#menu-drawer',
    '#Details-menu-drawer-container',
    'cart-drawer',
    '.cart-drawer',
    '[data-cart-drawer]',
    '#shopify-section-header',
    '.shopify-section-header',
    '.shopify-preview-bar',
    '#preview-bar-iframe',
    '#admin-bar-iframe',
  ].join(','));
}

function fjIsVisibleElement(element) {
  if (!(element instanceof HTMLElement)) return false;
  const style = window.getComputedStyle(element);
  const rect = element.getBoundingClientRect();

  return (
    rect.width > 0 &&
    rect.height > 0 &&
    style.display !== 'none' &&
    style.visibility !== 'hidden' &&
    Number(style.opacity || 1) !== 0
  );
}

function fjIsBottomRightFloating(element) {
  if (!(element instanceof HTMLElement)) return false;
  if (fjIsInsideProtectedArea(element)) return false;
  if (!fjIsVisibleElement(element)) return false;

  const style = window.getComputedStyle(element);
  if (style.position !== 'fixed' && style.position !== 'sticky') return false;

  const rect = element.getBoundingClientRect();
  const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

  const nearRight = rect.right >= viewportWidth - 140 || rect.left >= viewportWidth * 0.58;
  const nearBottom = rect.bottom >= viewportHeight - 230 || rect.top >= viewportHeight * 0.48;
  const plausibleWidgetSize = rect.width >= 24 && rect.height >= 24 && rect.width <= 430 && rect.height <= 260;

  return nearRight && nearBottom && plausibleWidgetSize;
}

function fjHasSupportWidgetMarker(element) {
  const marker = fjElementMarker(element);

  return /whats|wa\.me|api\.whatsapp|wa-widget|joinchat|wame|whatsapp-chat|shopify-chat|chat|crisp|tidio|gorgias|help|support|message|messenger/.test(marker);
}

function fjFindFloatingContainer(element) {
  if (!(element instanceof HTMLElement)) return null;

  let current = element;
  let fallback = null;

  while (current && current !== document.body && current !== document.documentElement) {
    if (fjIsInsideProtectedArea(current)) return fallback;

    if (fjIsBottomRightFloating(current)) {
      fallback = current;
    }

    current = current.parentElement;
  }

  return fallback;
}

function fjHideFloatingElement(element) {
  if (!(element instanceof HTMLElement)) return;
  if (element.hasAttribute(FJ_MENU_WIDGET_HIDE_ATTR)) return;

  element.setAttribute(FJ_MENU_WIDGET_HIDE_ATTR, 'true');
  element.setAttribute('data-fj-old-display', element.style.getPropertyValue('display') || '');
  element.setAttribute('data-fj-old-display-priority', element.style.getPropertyPriority('display') || '');
  element.setAttribute('data-fj-old-visibility', element.style.getPropertyValue('visibility') || '');
  element.setAttribute('data-fj-old-visibility-priority', element.style.getPropertyPriority('visibility') || '');
  element.setAttribute('data-fj-old-opacity', element.style.getPropertyValue('opacity') || '');
  element.setAttribute('data-fj-old-opacity-priority', element.style.getPropertyPriority('opacity') || '');
  element.setAttribute('data-fj-old-pointer-events', element.style.getPropertyValue('pointer-events') || '');
  element.setAttribute('data-fj-old-pointer-events-priority', element.style.getPropertyPriority('pointer-events') || '');

  element.classList.add('fj-force-hide-floating');
  element.style.setProperty('display', 'none', 'important');
  element.style.setProperty('visibility', 'hidden', 'important');
  element.style.setProperty('opacity', '0', 'important');
  element.style.setProperty('pointer-events', 'none', 'important');
}

function fjRestoreFloatingElement(element) {
  if (!(element instanceof HTMLElement)) return;

  const restoreProperty = (property) => {
    const value = element.getAttribute(`data-fj-old-${property}`) || '';
    const priority = element.getAttribute(`data-fj-old-${property}-priority`) || '';

    if (value) {
      element.style.setProperty(property, value, priority);
    } else {
      element.style.removeProperty(property);
    }

    element.removeAttribute(`data-fj-old-${property}`);
    element.removeAttribute(`data-fj-old-${property}-priority`);
  };

  restoreProperty('display');
  restoreProperty('visibility');
  restoreProperty('opacity');
  restoreProperty('pointer-events');

  element.classList.remove('fj-force-hide-floating');
  element.removeAttribute(FJ_MENU_WIDGET_HIDE_ATTR);
}

function fjSetFloatingSupportWidgetsHidden(isOpen) {
  if (!isOpen) {
    document.querySelectorAll(`[${FJ_MENU_WIDGET_HIDE_ATTR}="true"], .fj-force-hide-floating`).forEach(fjRestoreFloatingElement);
    return;
  }

  const explicitSelectors = [
    '#ShopifyChat',
    '#shopify-chat',
    '#chat-widget-container',
    '#tidio-chat',
    '#crisp-chatbox',
    '#wa-widget-send-button',
    '#whatsapp-chat-widget',
    '#whatsapp-widget',
    '.wa__btn_popup',
    '.wa__widget_container',
    '.wa-widget',
    '.whatsapp-widget',
    '.whatsapp-button',
    '.whatsapp_float',
    '.whatsapp-floating',
    '[id*="whatsapp" i]',
    '[class*="whatsapp" i]',
    '[id*="wa-widget" i]',
    '[class*="wa-widget" i]',
    '[href*="wa.me"]',
    '[href*="api.whatsapp"]',
    '[href*="whatsapp"]',
    'iframe[src*="whatsapp" i]',
    'iframe[title*="whatsapp" i]',
    'iframe[src*="chat" i]',
    'iframe[title*="chat" i]',
  ];

  document.querySelectorAll(explicitSelectors.join(',')).forEach((element) => {
    if (!(element instanceof HTMLElement) || fjIsInsideProtectedArea(element)) return;
    fjHideFloatingElement(fjFindFloatingContainer(element) || element);
  });

  document.querySelectorAll('body *').forEach((element) => {
    if (!(element instanceof HTMLElement) || fjIsInsideProtectedArea(element)) return;

    const floatingContainer = fjFindFloatingContainer(element);

    if (floatingContainer && (fjIsBottomRightFloating(element) || fjHasSupportWidgetMarker(element) || fjHasSupportWidgetMarker(floatingContainer))) {
      fjHideFloatingElement(floatingContainer);
    }
  });
}

function fjIsHeaderDrawerOpen(drawer) {
  return !!drawer?.refs?.details?.hasAttribute('open') || !!drawer?.refs?.details?.classList?.contains('menu-open');
}

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
    fjSetMobileMenuWidgetState(fjIsHeaderDrawerOpen(this));
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {Event} [event]
   */
  open(event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');
    fjSetMobileMenuWidgetState(true);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');
      setTimeout(() => {
        trapFocus(details);
      }, 0);
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');

    if (details === this.refs.details) {
      fjSetMobileMenuWidgetState(false);
    }

    onAnimationEnd(details, () => {
      reset(details);

      if (details === this.refs.details) {
        removeTrapFocus();
        const openDetails = this.querySelectorAll('details[open]');
        openDetails.forEach(reset);
      } else {
        setTimeout(() => {
          trapFocus(this.refs.details);
        }, 0);
      }
    });
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    /**
     * @param {AnimationEvent} event
     */
    function removeWillChangeOnAnimationEnd(event) {
      const target = event.target;
      if (target && target instanceof HTMLElement) {
        target.style.setProperty('will-change', 'unset');
        target.removeEventListener('animationend', removeWillChangeOnAnimationEnd);
      }
    }
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
